import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  authedFetch,
  hasStoredSession,
  type StoredAuth,
  useStoredAuth,
} from "nazare-ui/utils";
import { resolveBffDomain } from "./bff-domain";

export type SelfNotificationChannel = {
  id: string;
  channel_type: string;
  address: string;
  label: string | null;
  verified: boolean;
};

export type SelfNotificationPreference = {
  id: string;
  notification_type: string;
  reference_id: string | null;
  channel_type: string;
  enabled: boolean;
};

export type SelfPreferenceEdit = {
  notificationType: string;
  channelType: string;
  enabled: boolean;
};

const NOTIFICATIONS_BASE = `${resolveBffDomain()}/notifications`;
const channelsKey = ["settings", "me", "notification-channels"] as const;
const preferencesKey = ["settings", "me", "notification-preferences"] as const;

async function fetchChannels(auth: StoredAuth | null) {
  const res = await authedFetch(NOTIFICATIONS_BASE, auth, "/channels");
  if (!res.ok) throw new Error(`Failed to fetch notification channels: ${res.status}`);
  return res.json() as Promise<{ channels: SelfNotificationChannel[] }>;
}

async function fetchPreferences(auth: StoredAuth | null) {
  const res = await authedFetch(NOTIFICATIONS_BASE, auth, "/preferences");
  if (!res.ok) throw new Error(`Failed to fetch notification preferences: ${res.status}`);
  return res.json() as Promise<{ preferences: SelfNotificationPreference[] }>;
}

export function useSelfNotificationChannels() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: channelsKey,
    queryFn: () => fetchChannels(auth),
    enabled: hasStoredSession(auth),
    staleTime: 60_000,
  });
}

export function useSelfNotificationPreferences() {
  const auth = useStoredAuth();
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => fetchPreferences(auth),
    enabled: hasStoredSession(auth),
    staleTime: 30_000,
  });
}

export function useSetSelfNotificationPreference() {
  const auth = useStoredAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (edit: SelfPreferenceEdit) => {
      const path = `/preferences/${encodeURIComponent(edit.notificationType)}/_/${encodeURIComponent(edit.channelType)}`;
      const res = await authedFetch(NOTIFICATIONS_BASE, auth, path, {
        method: "PUT",
        body: JSON.stringify({ enabled: edit.enabled }),
      });
      if (!res.ok) throw new Error(`Failed to update notification preference: ${res.status}`);
      return res.json();
    },
    onMutate: async (edit) => {
      await queryClient.cancelQueries({ queryKey: preferencesKey });
      const previous = queryClient.getQueryData<{ preferences: SelfNotificationPreference[] }>(preferencesKey);
      queryClient.setQueryData<{ preferences: SelfNotificationPreference[] }>(preferencesKey, (current) => {
        const preferences = current?.preferences ?? [];
        const match = preferences.find(
          (preference) =>
            preference.notification_type === edit.notificationType &&
            preference.reference_id === null &&
            preference.channel_type === edit.channelType,
        );
        if (match) {
          return {
            preferences: preferences.map((preference) =>
              preference === match ? { ...preference, enabled: edit.enabled } : preference,
            ),
          };
        }
        return {
          preferences: [
            ...preferences,
            {
              id: `optimistic-${edit.notificationType}-${edit.channelType}`,
              notification_type: edit.notificationType,
              reference_id: null,
              channel_type: edit.channelType,
              enabled: edit.enabled,
            },
          ],
        };
      });
      return { previous };
    },
    onError: (_error, _edit, context) => {
      if (context?.previous) queryClient.setQueryData(preferencesKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: preferencesKey }),
  });
}
